// Página de restablecimiento de contraseña (tema oscuro SACS). Se llega desde el
// enlace del correo (/reset-password?token=…). Pide la nueva contraseña.
import type { JSX, ReactNode } from "react";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, ApiError } from "../lib/api.ts";

const BG = {
  background: [
    "radial-gradient(55% 55% at 12% 8%, rgba(97,44,132,0.45), transparent 60%)",
    "radial-gradient(45% 45% at 96% 28%, rgba(0,158,226,0.22), transparent 60%)",
    "radial-gradient(85% 85% at 55% 125%, rgba(33,56,109,0.55), transparent 60%)",
    "linear-gradient(160deg, #0d1424 0%, #10182b 45%, #0b1120 100%)",
  ].join(","),
};
const CARD_GLOW = { boxShadow: "0 0 0 1px rgba(237,113,33,0.45), 0 10px 40px rgba(237,113,33,0.12), 0 24px 70px rgba(0,0,0,0.55)" };

export function ResetPassword(): JSX.Element {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = (e: React.FormEvent): void => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError("La contraseña debe tener al menos 8 caracteres."); return; }
    if (password !== confirm) { setError("Las contraseñas no coinciden."); return; }
    setSaving(true);
    api.post("/auth/reset-password", { token, password })
      .then(() => setDone(true))
      .catch((err) => setError(err instanceof ApiError ? err.message : "No se pudo restablecer la contraseña."))
      .finally(() => setSaving(false));
  };

  return (
    <div className="min-h-screen relative flex flex-col items-center justify-center p-4 text-white" style={BG}>
      <img src="/logo-blanco.png" alt="SACS Group" className="absolute top-6 left-6 h-10 w-auto object-contain" />
      <div className="flex flex-col items-center mb-6">
        <img src="/tigre.jpg" alt="Tiger" className="w-24 h-24 rounded-full object-cover ring-2 ring-accent" style={{ boxShadow: "0 0 32px rgba(237,113,33,0.55)" }} />
        <h1 className="mt-4 text-2xl font-bold tracking-tight">Tiger</h1>
        <p className="text-sm text-brand-cyan">SACS Consultores</p>
      </div>

      <div className="w-full max-w-sm rounded-2xl bg-[#111a2e]/80 backdrop-blur-sm border border-white/5 p-7" style={CARD_GLOW}>
        {!token ? (
          <div className="text-center">
            <h2 className="text-lg font-semibold mb-2">Enlace inválido</h2>
            <p className="text-sm text-gray-300 mb-5">Falta el token del enlace. Solicita un nuevo restablecimiento desde el inicio de sesión.</p>
            <button className="btn-accent w-full" onClick={() => navigate("/login")}>Ir a iniciar sesión</button>
          </div>
        ) : done ? (
          <div className="text-center">
            <h2 className="text-lg font-semibold mb-2">Contraseña actualizada</h2>
            <p className="text-sm text-gray-300 mb-5">Ya puedes iniciar sesión con tu nueva contraseña.</p>
            <button className="btn-accent w-full" onClick={() => navigate("/login")}>Iniciar sesión</button>
          </div>
        ) : (
          <>
            <h2 className="text-lg font-semibold mb-5">Nueva contraseña</h2>
            <form onSubmit={submit} className="space-y-4">
              <DarkField label="Nueva contraseña" icon={<LockIcon />}
                trailing={
                  <button type="button" className="text-gray-400 hover:text-gray-600" onClick={() => setShowPw((v) => !v)} aria-label="Mostrar/ocultar">
                    <EyeIcon off={showPw} />
                  </button>
                }
              >
                <input type={showPw ? "text" : "password"} className="dark-input pr-9" value={password} onChange={(e) => setPassword(e.target.value)} autoFocus required />
              </DarkField>
              <DarkField label="Confirmar contraseña" icon={<LockIcon />}>
                <input type={showPw ? "text" : "password"} className="dark-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
              </DarkField>
              {error && <p className="text-sm text-red-400">{error}</p>}
              <button type="submit" className="btn-accent w-full" disabled={saving}>{saving ? "Guardando…" : "Restablecer contraseña"}</button>
            </form>
          </>
        )}
      </div>
      <p className="mt-8 text-xs text-gray-500">© 2026 SACS Consultores</p>
    </div>
  );
}

function DarkField({ label, icon, trailing, children }: { label: string; icon: ReactNode; trailing?: ReactNode; children: ReactNode }): JSX.Element {
  return (
    <div>
      <label className="block text-xs text-gray-300 mb-1.5">{label}</label>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">{icon}</span>
        {children}
        {trailing && <span className="absolute right-2.5 top-1/2 -translate-y-1/2">{trailing}</span>}
      </div>
    </div>
  );
}
function LockIcon(): JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4.5" y="10.5" width="15" height="10" rx="2" /><path d="M8 10.5V7a4 4 0 018 0v3.5" />
    </svg>
  );
}
function EyeIcon({ off }: { off: boolean }): JSX.Element {
  return off ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 3l18 18" /><path d="M10.6 10.6a2 2 0 002.8 2.8" /><path d="M9.4 5.1A9.5 9.5 0 0112 5c4.6 0 8.6 3 10 7a12 12 0 01-2.2 3.4M6.1 6.1A12 12 0 002 12c1.4 4 5.4 7 10 7a9.5 9.5 0 003.5-.7" />
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7z" /><circle cx="12" cy="12" r="3" />
    </svg>
  );
}
